import React, { useState } from 'react';
import { Button, Empty, Spin, Alert } from 'antd';
import { FiPhone, FiRefreshCw } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { CallLog } from '@/types/TCRMLogs';
import { Customer } from '@/types/TCRMLead';
import { CallDetailView } from './CallDetailView';
import { useCRMCallLogs } from 'src/Hooks/SmartCRM/useCRMLogs';
import { getProvisionedNumber } from '@/api/reservedNumbers';

interface CallLogsProps {
	customer: Customer;
	clientID: string;
	isNoviUser?: boolean;
}

export const CallLogs: React.FC<CallLogsProps> = ({ customer, clientID, isNoviUser = false }) => {
	const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);

	const leadPhoneNumber = customer.number || 'N/A';

	const {
		data: provisionedNumber,
		isLoading: isLoadingNumber,
		error: numberError,
	} = useQuery({
		queryKey: ['getProvisionedNumbers', clientID],
		queryFn: () => getProvisionedNumber(clientID),
		enabled: !!clientID,
		retry: 2,
	});

	const businessPhoneNumber = provisionedNumber?.provisioned_number || '';

	const { callLogs, loading, error, hasMore, fetchCallLogs, loadMore } = useCRMCallLogs(
		clientID,
		leadPhoneNumber,
		businessPhoneNumber,
		isNoviUser,
	);

	const formatDuration = (seconds: string) => {
		const secs = parseInt(seconds);
		const mins = Math.floor(secs / 60);
		const remainingSecs = secs % 60;
		return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
	};

	const getCallDirection = (call: CallLog) => {
		if (call.from === leadPhoneNumber) {
			return 'Inbound Call';
		} else if (call.to === leadPhoneNumber) {
			return 'Outbound Call';
		}
		return 'Unknown Direction';
	};

	if (selectedCall) {
		return <CallDetailView call={selectedCall} onBack={() => setSelectedCall(null)} />;
	}

	if (isLoadingNumber) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" />
				<span className="ml-2 text-gray-500">Loading business number...</span>
			</div>
		);
	}

	if (numberError) {
		return (
			<Alert
				message="Error Loading Business Number"
				description="Unable to fetch your business phone number. Please try again."
				type="warning"
				action={
					<Button size="small" onClick={() => window.location.reload()}>
						Reload
					</Button>
				}
			/>
		);
	}

	if (!businessPhoneNumber) {
		return (
			<Alert
				message="No Business Number Found"
				description="No provisioned phone number found for this client. Call logs require a business number to display properly."
				type="info"
			/>
		);
	}

	if (loading && callLogs.length === 0) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" />
				<span className="ml-2 text-gray-500">Loading call logs...</span>
			</div>
		);
	}

	if (error) {
		return (
			<Alert
				message="Error"
				description={error}
				type="error"
				action={
					<Button size="small" onClick={() => fetchCallLogs(true)}>
						Retry
					</Button>
				}
			/>
		);
	}

	if (callLogs.length === 0) {
		return (
			<div className="text-center py-8">
				<Empty
					description={
						<div>
							<p>No call logs found between:</p>
							<p className="text-sm text-gray-500 mt-2">
								Lead: {leadPhoneNumber} ↔ Business: {businessPhoneNumber}
							</p>
						</div>
					}
				/>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<h3 className="text-lg font-medium">Call History ({callLogs.length})</h3>
				<div className="flex items-center gap-2">
					<div className="text-xs text-gray-500">
						Lead: {leadPhoneNumber} | Business: {businessPhoneNumber}
					</div>
					<Button
						onClick={() => fetchCallLogs(true)}
						loading={loading}
						icon={<FiRefreshCw />}
						type="text"
						size="small"
					>
						Refresh
					</Button>
				</div>
			</div>

			{callLogs.map((call) => {
				const direction = getCallDirection(call);
				const isRecorded = call.isRecorded === 'true';

				return (
					<div
						key={call.callSid}
						onClick={() => setSelectedCall(call)}
						className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow"
					>
						<div className="flex items-center gap-4">
							<div
								className={`w-12 h-12 rounded-full flex items-center justify-center ${
									direction.includes('Inbound')
										? 'text-green-600 bg-green-50'
										: 'text-blue-600 bg-blue-50'
								}`}
							>
								<FiPhone className="w-6 h-6" />
							</div>
							<div className="flex-1">
								<div className="flex items-center justify-between mb-2">
									<h4 className="font-medium text-gray-900">{direction}</h4>
									<div className="flex items-center gap-2">
										<span className="text-sm text-gray-500">{formatDuration(call.duration)}</span>
										{isRecorded && (
											<div className="w-2 h-2 bg-red-500 rounded-full" title="Call recorded" />
										)}
									</div>
								</div>
								<div className="flex items-center gap-4 mb-2">
									<span className="text-sm text-gray-600">
										{new Date(call.startedTimestamp).toLocaleString()}
									</span>
									<span className="text-sm text-gray-600">•</span>
									<span className="text-sm text-gray-600">
										{call.from} → {call.to}
									</span>
								</div>
								{call.callSummary && (
									<p className="text-sm text-gray-700 line-clamp-2">
										{call.callSummary.replace(/"/g, '')}
									</p>
								)}
							</div>
						</div>
					</div>
				);
			})}

			{hasMore && (
				<div className="flex justify-center pt-4">
					<Button onClick={loadMore} loading={loading} type="primary" disabled={!hasMore}>
						{loading ? 'Loading...' : 'Load More'}
					</Button>
				</div>
			)}
		</div>
	);
};
